import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import {
  ClinicContextStoreEventData,
  IClinicContextStore,
} from '@surglogs-app/types'

import { ClinicContext, IClinicContext } from '../context'
import { setClinicIdToStorage } from '../helpers'

import { useAuthContextData } from './useAuthContext'
import { useAuthToken } from './useAuthToken'

export const useClinicContext = () => useContext<IClinicContext>(ClinicContext)

export const ClinicContextProvider = ({
  children,
  store,
}: {
  children: ReactNode
  store: IClinicContextStore
}) => {
  const { token } = useAuthToken()
  const { userId } = useAuthContextData()
  const [value, setValue] = useState<{
    clinic: ClinicContextStoreEventData
    clinicId: number | null
  }>({ clinic: store.getData(), clinicId: store.getClinicId() })

  useEffect(() => {
    setValue({ clinic: store.getData(), clinicId: store.getClinicId() })
    return store.onChange((data) => {
      if (typeof data === 'number') {
        setValue((oldValue) => ({
          clinic: oldValue.clinic?.id === data ? oldValue.clinic : null,
          clinicId: data,
        }))
      } else {
        setValue({ clinic: data, clinicId: data?.id ?? null })
      }
    })
  }, [store])

  const changeClinic = useCallback(
    (clinicId: number, tokenParam?: string | null) => {
      if (tokenParam) {
        store.changeClinic(clinicId, tokenParam)
      } else if (token) {
        store.changeClinic(clinicId, token)
      }
    },
    [store, token],
  )

  const refetchClinicData = useCallback(() => {
    if (token) {
      store.refetchClinicData(token)
    }
  }, [store, token])

  const setClinicId = useCallback(
    (clinicId: number, withoutStorageSync: boolean = false) => {
      if (!withoutStorageSync) {
        setClinicIdToStorage(clinicId, userId)
      }
      store.setClinicId(clinicId)
      changeClinic(clinicId)
    },
    [store, changeClinic, userId],
  )

  const clearClinicData = useCallback(() => {
    store.clear()
  }, [store])

  return (
    <ClinicContext.Provider
      {...{
        value: {
          clinic: value.clinic,
          clinicId: value.clinicId,
          changeClinic,
          refetchClinicData,
          setClinicId,
          clearClinicData,
        },
      }}>
      {children}
    </ClinicContext.Provider>
  )
}
